import { Injectable, NgZone, OnDestroy, inject } from '@angular/core';
import { Subject, fromEvent, merge } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

/**
 * Servicio de detección de inactividad del usuario.
 *
 * Detecta cuando el usuario está inactivo durante un período configurable
 * y emite un evento para manejar el timeout de sesión.
 */
@Injectable({
  providedIn: 'root'
})
export class IdleService implements OnDestroy {
  private ngZone = inject(NgZone);

  // Configuración (en milisegundos)
  private readonly IDLE_TIMEOUT = 60 * 60 * 1000; // 30 minutos
  
  private readonly CHECK_INTERVAL = 1000; // Verificar cada segundo

  // Subjects para manejar eventos
  private destroy$ = new Subject<void>();
  private idle$ = new Subject<void>();

  // Timer de inactividad
  private idleTimer: any;
  private lastActivity: number = Date.now();

  constructor() {
    this.setupActivityListeners();
    this.startIdleCheck();
  }

  /**
   * Observable que emite cuando el usuario está inactivo
   */
  get onIdle() {
    return this.idle$.asObservable();
  }

  /**
   * Configura los listeners de actividad del usuario
   */
  private setupActivityListeners(): void {
    // Ejecutar fuera de Angular zone para mejor performance
    this.ngZone.runOutsideAngular(() => {
      // Eventos que indican actividad del usuario
      const activityEvents = [
        'mousedown',
        'mousemove',
        'keypress',
        'scroll',
        'touchstart',
        'click',
        'wheel'
      ];

      // Combinar todos los eventos en un solo observable
      const events$ = merge(
        ...activityEvents.map(event =>
          fromEvent(document, event)
        )
      );

      // Actualizar timestamp de última actividad (con debounce para performance)
      events$
        .pipe(
          debounceTime(500), // Evitar demasiadas actualizaciones
          takeUntil(this.destroy$)
        )
        .subscribe(() => {
          this.resetIdleTimer();
        });
    });
  }

  /**
   * Inicia el verificador periódico de inactividad
   */
  private startIdleCheck(): void {
    this.idleTimer = setInterval(() => {
      this.checkIdleTimeout();
    }, this.CHECK_INTERVAL);
  }

  /**
   * Verifica si el usuario ha estado inactivo por más tiempo del permitido
   */
  private checkIdleTimeout(): void {
    const now = Date.now();
    const timeSinceLastActivity = now - this.lastActivity;

    if (timeSinceLastActivity >= this.IDLE_TIMEOUT) {
      console.warn('[Idle] User has been inactive for 30 minutes, triggering timeout');
      this.triggerIdle();
    }
  }

  /**
   * Resetea el timer de inactividad
   */
  resetIdleTimer(): void {
    this.lastActivity = Date.now();
  }

  /**
   * Dispara el evento de inactividad
   */
  private triggerIdle(): void {
    // Emitir evento de inactividad dentro de Angular zone
    this.ngZone.run(() => {
      this.idle$.next();
    });

    // Detener verificaciones hasta que se reinicie manualmente
    this.stopIdleCheck();
  }

  /**
   * Detiene la verificación de inactividad
   */
  private stopIdleCheck(): void {
    if (this.idleTimer) {
      clearInterval(this.idleTimer);
      this.idleTimer = null;
    }
  }

  /**
   * Reinicia el servicio de inactividad
   * Útil después de hacer login nuevamente
   */
  restart(): void {
    this.resetIdleTimer();
    if (!this.idleTimer) {
      this.startIdleCheck();
    }
  }

  /**
   * Detiene el servicio completamente
   * Útil al hacer logout
   */
  stop(): void {
    this.stopIdleCheck();
    this.destroy$.next();
  }

  /**
   * Limpieza al destruir el servicio
   */
  ngOnDestroy(): void {
    this.stop();
    this.destroy$.complete();
    this.idle$.complete();
  }
}
